import { useMemo, useState } from 'react'
import { Alert, Box, Button, Stack, Tab, Tabs, TextField, Typography } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import {
  useClonableSources,
  usePolicyConfigurations,
  type PolicyConfigurationRow,
} from './BenefitPolicyService'
import BenefitPolicyEditor from './BenefitPolicyEditor'
import { BenefitPolicyList, BenefitPolicyListEmpty } from './BenefitPolicyList'
import { ClonePlanYearDialog } from './ClonePlanYearDialog'
import { NewPolicyDialog } from './NewPolicyDialog'
import { isHistorical, isUpcoming, planYearTabs, type PlanYearTabKey } from './planYears'
import { required } from '~/shared/lib/required'

/** The Benefit Policies settings screen: plan configurations by plan year. */
const BenefitPolicySettings = () => {
  const currentYear = new Date().getFullYear()
  const tabs = useMemo(() => planYearTabs(currentYear), [currentYear])

  const [tabKey, setTabKey] = useState<PlanYearTabKey>('current')
  const [contractFilter, setContractFilter] = useState('')
  const [newOpen, setNewOpen] = useState(false)
  const [cloneSourceId, setCloneSourceId] = useState<number | undefined>()
  const [cloneOpen, setCloneOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | undefined>()

  const activeTab = required(tabs.find(tab => tab.key === tabKey) ?? tabs[0], 'a plan-year tab')
  const { data, isPending: isLoading, isError } = usePolicyConfigurations(activeTab.filters)
  const { data: sourceData } = useClonableSources()

  const canEdit = data?.can_edit ?? false
  const allPolicies = useMemo(() => data?.policies ?? [], [data])

  const rows = useMemo(() => {
    // Two of the three tabs are open-ended ranges rather than a single `plan_year` the endpoint
    // can filter on, so they read the whole collection and are narrowed here. Together the three
    // cover every year: a configuration further out than next year still has a tab that shows it.
    const forTab =
      tabKey === 'historical'
        ? allPolicies.filter(row => isHistorical(row.effective_start, currentYear))
        : tabKey === 'next'
          ? allPolicies.filter(row => isUpcoming(row.effective_start, currentYear))
          : allPolicies
    const needle = contractFilter.trim().toLowerCase()
    if (!needle) return forTab
    return forTab.filter(row => (row.contract_name ?? '').toLowerCase().includes(needle))
  }, [allPolicies, tabKey, currentYear, contractFilter])

  // From the unfiltered pool, not the visible tab: renewal cloning is done while looking at next
  // year, and the source lives in the current one.
  const cloneSources = useMemo(
    () => (sourceData?.policies ?? []).filter(row => row.can_clone),
    [sourceData],
  )

  const openClone = (row?: PolicyConfigurationRow) => {
    setCloneSourceId(row?.id)
    setCloneOpen(true)
  }

  const openEditor = (id: number) => setEditingId(id)

  /**
   * Show the tab the new draft actually landed in.
   *
   * A new policy — and always a clone — usually belongs to a plan year other than the one on
   * screen, so without this the list looks unchanged and the write reads as a no-op. Anything
   * older than this year is on the Historical tab.
   */
  const revealCreated = (created: { id: number; effective_start: string | null }) => {
    const year = created.effective_start
      ? new Date(created.effective_start).getUTCFullYear()
      : currentYear
    // Every year has a tab now that the upcoming one is open-ended, so this always lands
    // somewhere — no silent no-op for a draft further out than next year.
    setTabKey(year > currentYear ? 'next' : year < currentYear ? 'historical' : 'current')
    setContractFilter('')
    openEditor(created.id)
  }

  // The editor replaces the list rather than sitting beside it: the sections are long, and a
  // half-visible list behind them competes for the same scroll.
  if (editingId != null) {
    return <BenefitPolicyEditor policyId={editingId} onBack={() => setEditingId(undefined)} />
  }

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      <Stack direction="row" spacing={2} sx={{ alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            Benefit policies
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 640, mt: 0.5 }}>
            Policy configurations by plan year. Create a new policy for an onboarding client, or
            clone a current policy forward for renewal — backend validations run before anything is
            published.
          </Typography>
        </Box>
        {canEdit && (
          <Stack direction="row" spacing={1.5} sx={{ flex: 'none' }}>
            <Button
              variant="outlined"
              startIcon={<ContentCopyIcon />}
              onClick={() => openClone()}
              disabled={cloneSources.length === 0}
              data-testid="open-clone-dialog"
            >
              Clone a plan year
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setNewOpen(true)}
              data-testid="open-new-policy-dialog"
            >
              New policy
            </Button>
          </Stack>
        )}
      </Stack>

      {!isLoading && !canEdit && (
        <Alert severity="info" sx={{ mt: 2 }} data-testid="read-only-banner">
          You have read-only access. Editing is limited to the Implementation Ops group.
        </Alert>
      )}

      <Tabs
        value={tabKey}
        onChange={(_event, value: PlanYearTabKey) => setTabKey(value)}
        sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        {tabs.map(tab => (
          <Tab key={tab.key} value={tab.key} label={tab.label} data-testid={`tab-${tab.key}`} />
        ))}
      </Tabs>

      <Stack direction="row" spacing={2} sx={{ alignItems: 'center', my: 2 }}>
        <TextField
          size="small"
          label="Filter contracts"
          value={contractFilter}
          onChange={event => setContractFilter(event.target.value)}
          sx={{ width: 280 }}
          data-testid="contract-filter"
        />
        <Typography variant="body2" sx={{ color: 'text.secondary', ml: 'auto' }}>
          {rows.length} {rows.length === 1 ? 'policy' : 'policies'}
        </Typography>
      </Stack>

      {isError ? (
        <Alert severity="error" sx={{ mt: 2 }} data-testid="list-error">
          Could not load benefit policies. Refresh to try again.
        </Alert>
      ) : !isLoading && rows.length === 0 ? (
        <BenefitPolicyListEmpty />
      ) : (
        <BenefitPolicyList
          rows={rows}
          loading={isLoading}
          onOpen={openEditor}
          onClone={openClone}
        />
      )}

      <NewPolicyDialog
        open={newOpen}
        onClose={() => setNewOpen(false)}
        onCreated={created => {
          setNewOpen(false)
          revealCreated(created)
        }}
      />
      <ClonePlanYearDialog
        open={cloneOpen}
        sources={cloneSources}
        initialSourceId={cloneSourceId}
        onClose={() => setCloneOpen(false)}
        onCloned={created => {
          setCloneOpen(false)
          revealCreated(created)
        }}
      />
    </Box>
  )
}

export default BenefitPolicySettings
